import * as THREE from 'three';
import { COUNTRIES } from '../data/countries.js';

// ==========================================================
// 👇 EASY CONTROLS – change these to tune flag appearance
// ==========================================================

// ===== GEOMETRY DETAIL =====
// 2 = Subdivide by 2 (Creates a 3x3 vertex grid). 
// Note: For a smooth "hugging" look, 8 or 16 is usually better.
const FLAG_SUBDIVISIONS = 4; 

// How much the flag "hugs" the dome. 
// 1.0 = mathematically perfect wrap. 3.0+ = exaggerated "taking shape".
const BEND_STRENGTH = 4.6; 
// ==========================

const FLAG_SIZE_MULTIPLIER = 1.5;
const HORIZONTAL_SPEED = 15;
const VERTICAL_SPEED   = 12;
const VERTICAL_MIN_OFFSET = 0;
const VERTICAL_MAX_OFFSET = 5;
const VERTICAL_AMPLITUDE = 0.5;
const VERTICAL_BIAS = 0.1;

// ==========================================================
// (Standard Scene Constants)
// ==========================================================

const DOME_RADIUS      = 285;
const DOME_CENTER_Y    = -10;
const MAX_FLAG_SCALE   = 0.56 * FLAG_SIZE_MULTIPLIER;
const SUPPORTERS_PER_FLAG = 10000;
const POISSON_MIN_DIST = 52 * FLAG_SIZE_MULTIPLIER;
const SAME_COUNTRY_MIN = 60 * FLAG_SIZE_MULTIPLIER;
const COS_MIN          = 0.38;

const _workVec = new THREE.Vector3();

// --- THE GEOMETRY WRAPPER ---
// Manually manipulates the subdivided vertex buffer to curve the plane
function applySphericalWrap(geometry, meshScale) {
  const pos = geometry.attributes.position;
  const v = new THREE.Vector3();
  
  // The local radius needs to be adjusted by the mesh scale to stay "real"
  // We multiply by BEND_STRENGTH to ensure the shape is visible.
  const localRadius = (DOME_RADIUS / (meshScale || 1.0)) / BEND_STRENGTH;

  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    
    // We treat X and Y as arc-lengths on the sphere
    const theta = v.x / localRadius;
    const phi = v.y / localRadius;
    
    // Spherical projection (Cylindrical-ish for better flag visibility)
    // We move Z to create the "bend"
    const newX = localRadius * Math.sin(theta);
    const newY = v.y; // Keep Y linear or wrap it too: localRadius * Math.sin(phi)
    const newZ = localRadius * (Math.cos(theta) - 1.0); 

    pos.setXYZ(i, newX, newY, newZ);
  }
  
  pos.needsUpdate = true;
  geometry.computeVertexNormals();
}

// --- UTILITIES (Curl Noise & Poisson) ---
function n3(x, y, z) {
  const ix = Math.floor(x), iy = Math.floor(y), iz = Math.floor(z);
  const fx = x - ix, fy = y - iy, fz = z - iz;
  const f = t => t * t * t * (t * (t * 6 - 15) + 10);
  const r = (a, b, c) => (Math.sin(a * 127.1 + b * 311.7 + c * 74.7) * 43758.5453) % 1;
  const lp = (a, b, t) => a + t * (b - a);
  return lp(lp(lp(r(ix, iy, iz), r(ix + 1, iy, iz), f(fx)), lp(r(ix, iy + 1, iz), r(ix + 1, iy + 1, iz), f(fx)), f(fy)), lp(lp(r(ix, iy, iz + 1), r(ix + 1, iy, iz + 1), f(fx)), lp(r(ix, iy + 1, iz + 1), r(ix + 1, iy + 1, iz + 1), f(fx)), f(fy)), f(fz));
}

function curl3(x, y, z, t = 0) {
  const e = 0.015, tx = t * 0.08;
  return new THREE.Vector3(
    (n3(x, y + e, z + tx) - n3(x, y - e, z + tx)) / (2 * e) - (n3(x, y + tx, z + e) - n3(x, y + tx, z - e)) / (2 * e),
    (n3(x + tx, y, z + e) - n3(x + tx, y, z - e)) / (2 * e) - (n3(x + e, y, z + tx) - n3(x - e, y, z + tx)) / (2 * e),
    (n3(x + e, y + tx, z) - n3(x - e, y + tx, z)) / (2 * e) - (n3(x, y + e, z + tx) - n3(x, y - e, z + tx)) / (2 * e)
  );
}

function generatePoissonPositions(capacity, radius) {
  const positions = [];
  const maxAttempts = 80 * capacity;
  let attempts = 0;
  while (positions.length < capacity && attempts < maxAttempts) {
    attempts++;
    const cosTheta = COS_MIN + (1.0 - COS_MIN) * Math.random();
    const sinTheta = Math.sqrt(Math.max(0, 1 - cosTheta * cosTheta));
    const phi = Math.random() * Math.PI * 2;
    const candidate = new THREE.Vector3(radius * sinTheta * Math.cos(phi), DOME_CENTER_Y + radius * cosTheta, radius * sinTheta * Math.sin(phi));
    let valid = true;
    for (const p of positions) { if (candidate.distanceTo(p) < POISSON_MIN_DIST) { valid = false; break; } }
    if (valid) positions.push(candidate);
  }
  return positions;
}

function calculateFlagSize(count, maxCount) {
  const baseSize = Math.max(4, 3 + 3.5 * Math.log(count + 1));
  const maxSize  = Math.max(4, 3 + 3.5 * Math.log(maxCount + 1)) * MAX_FLAG_SCALE;
  return Math.min(baseSize, maxSize) * FLAG_SIZE_MULTIPLIER;
}

export class FlagDome {
  constructor(scene) {
    this.scene = scene;
    this._positions = [];
    this._flags = [];
    this._pending = new Set();
    this._slotIdx = 0;
    this._nationalities = {};
    this._loader = new THREE.TextureLoader();
    this._texCache = {};
    this._flagsByNat = {};
  }

  init() {
    this._positions = generatePoissonPositions(700, DOME_RADIUS);
  }

  updateFlags(nationalities, smoothStage) {
    this._nationalities = nationalities;
    const maxCount = Math.max(...Object.values(nationalities), 1);
    for (const [nat, count] of Object.entries(nationalities)) {
      if (count > 0) {
        const flagsNeeded = Math.floor(count / SUPPORTERS_PER_FLAG) + 1;
        const existingFlags = (this._flagsByNat[nat] || []).length;
        if (existingFlags < flagsNeeded) {
          for (let i = existingFlags; i < flagsNeeded; i++) {
            const key = `${nat}_${i}`;
            if (!this._pending.has(key)) {
              this._pending.add(key);
              this._createFlag(nat, count, i, maxCount).finally(() => this._pending.delete(key));
            }
          }
        }
        this._updateScales(nat, count, maxCount);
      }
    }
  }

  addFlagForFirstContribution(nationality) {
    const key = `${nationality}_0`;
    if (this._pending.has(key) || this._flagsByNat[nationality]?.length > 0) return;
    this._pending.add(key);
    this._createFlag(nationality, 1, 0, 1).finally(() => this._pending.delete(key));
  }

  _updateScales(nat, count, maxCount) {
    const flagIndices = this._flagsByNat[nat] || [];
    const fullFlagCount = Math.floor(count / SUPPORTERS_PER_FLAG);
    const remainder = count % SUPPORTERS_PER_FLAG;
    flagIndices.forEach((idx, flagNum) => {
      const mesh = this._flags[idx];
      if (!mesh) return;
      mesh.userData.targetScale = calculateFlagSize(flagNum < fullFlagCount ? SUPPORTERS_PER_FLAG : remainder, maxCount);
    });
  }

  _getFlagSlot(nat) {
    for (let i = this._slotIdx; i < this._positions.length; i++) {
      const candidate = this._positions[i];
      const slot = this._slotIdx++;
      return slot;
    }
    return null;
  }

  async _createFlag(nat, count, flagIndex, maxCount) {
    const slot = this._getFlagSlot(nat);
    if (slot === null) return;

    const iso = COUNTRIES[nat];
    const homePos = this._positions[slot].clone();
    const texture = await this._loadTex(iso);
    const targetScale = calculateFlagSize(count, maxCount);

    // 1. Create Subdivided Geometry
    const geo = new THREE.PlaneGeometry(1.5, 1.0, FLAG_SUBDIVISIONS, FLAG_SUBDIVISIONS);
    
    // 2. Wrap the geometry vertices
    applySphericalWrap(geo, targetScale);

    const mat = new THREE.MeshBasicMaterial({
      map: texture,
      color: texture ? 0xffffff : 0xcccccc,
      transparent: true,
      side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(homePos);
    mesh.scale.setScalar(0.001); // Start small for entry animation

    mesh.userData = {
      homePos,
      velocity: new THREE.Vector3(),
      phase: Math.random() * Math.PI * 2,
      curlOffset: Math.random() * 100,
      targetScale,
      noBloom: true,
    };

    this._flags.push(mesh);
    this.scene.add(mesh);
    if (!this._flagsByNat[nat]) this._flagsByNat[nat] = [];
    this._flagsByNat[nat].push(this._flags.length - 1);
  }

  _loadTex(iso) {
    if (this._texCache[iso]) return Promise.resolve(this._texCache[iso]);
    return new Promise(resolve => {
      this._loader.load(`https://flagcdn.com/w80/${iso}.png`, 
        tex => { this._texCache[iso] = tex; resolve(tex); }, 
        undefined, () => resolve(null));
    });
  }

  update(time, smoothStage, delta) {
    const dt = Math.min(delta, 0.05);
    const center = new THREE.Vector3(0, DOME_CENTER_Y, 0);

    this._flags.forEach((mesh) => {
      const { homePos, velocity, phase, curlOffset, targetScale } = mesh.userData;

      const c = curl3(Math.cos(phase) * 0.4 + curlOffset, Math.sin(phase) * 0.4, curlOffset * 0.2, time * 0.12 * HORIZONTAL_SPEED);
      const driftTheta = phase + time * 0.06 + c.x * 0.25 * HORIZONTAL_SPEED;
      const driftPhi = Math.max(0.25, Math.min(Math.PI * 0.72, Math.PI/2 + c.y * 0.08 * HORIZONTAL_SPEED));
      const r = 7.5 + Math.sin(time * 0.4 + phase) * 0.3;
      const clampedOffset = Math.max(VERTICAL_MIN_OFFSET, Math.min(VERTICAL_MAX_OFFSET, (r * Math.cos(driftPhi) * 0.1 + 0.8) * VERTICAL_AMPLITUDE));

      let targetPos = new THREE.Vector3(
        homePos.x + r * Math.sin(driftPhi) * Math.cos(driftTheta) * 0.1,
        homePos.y + clampedOffset,
        homePos.z + r * Math.sin(driftPhi) * Math.sin(driftTheta) * 0.1
      );

      // Project target onto dome
      const dir = targetPos.clone().sub(center).normalize();
      targetPos.copy(center.clone().add(dir.multiplyScalar(DOME_RADIUS)));

      velocity.x += (targetPos.x - mesh.position.x) * 0.1 + c.x * 0.5;
      velocity.y += (targetPos.y - mesh.position.y) * 0.1 + c.y * 0.3;
      velocity.z += (targetPos.z - mesh.position.z) * 0.1 + c.z * 0.5;
      velocity.multiplyScalar(0.85);
      mesh.position.addScaledVector(velocity, dt * 30);

      // Final Clamp
      _workVec.copy(mesh.position).sub(center).normalize().multiplyScalar(DOME_RADIUS);
      mesh.position.copy(center.clone().add(_workVec));

      // Orientation
      mesh.lookAt(center);
      mesh.rotateY(Math.PI); 

      // Smooth Scale
      mesh.scale.setScalar(mesh.scale.x + (targetScale - mesh.scale.x) * 0.05);
    });
  }
}